import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet-draw";
import { MAP_CONFIG } from "../config/api";

function MapComponent({ activeLayer, onMapReady, onDrawCreated, children }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const analysisLayersRef = useRef(null);
  const osmLayerRef = useRef(null);
  const satelliteLayerRef = useRef(null);
  const labelsLayerRef = useRef(null);

  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Initialize map
      const map = L.map(mapRef.current, {
        drawControl: false,
        zoomControl: false,
      }).setView(MAP_CONFIG.DEFAULT_CENTER, MAP_CONFIG.DEFAULT_ZOOM);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      // Create tile layers
      osmLayerRef.current = L.tileLayer(MAP_CONFIG.OSM_TILE_URL, {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      });

      satelliteLayerRef.current = L.tileLayer(MAP_CONFIG.SATELLITE_TILE_URL, {
        attribution: "Tiles &copy; Esri",
      });

      labelsLayerRef.current = L.tileLayer(MAP_CONFIG.LABELS_TILE_URL, {
        attribution: "",
        pane: "shadowPane",
      });

      osmLayerRef.current.addTo(map);

      // Create feature groups
      drawnItemsRef.current = new L.FeatureGroup().addTo(map);
      analysisLayersRef.current = new L.FeatureGroup().addTo(map);

      // Context menu for coordinates
      map.on("contextmenu", (e) => {
        L.popup()
          .setLatLng(e.latlng)
          .setContent(
            `Lat: ${e.latlng.lat.toFixed(6)}, Lng: ${e.latlng.lng.toFixed(6)}`
          )
          .openOn(map);
      });

      // Handle draw created event
      map.on(L.Draw.Event.CREATED, (event) => {
        const layer = event.layer;
        drawnItemsRef.current.addLayer(layer);
        if (onDrawCreated) {
          onDrawCreated(layer);
        }
      });

      mapInstanceRef.current = map;

      // Notify parent that map is ready
      if (onMapReady) {
        onMapReady({
          map,
          drawnItems: drawnItemsRef.current,
          analysisLayers: analysisLayersRef.current,
        });
      }
    }

    // Handle layer switching
    if (mapInstanceRef.current) {
      const map = mapInstanceRef.current;

      if (activeLayer === "satellite") {
        if (map.hasLayer(osmLayerRef.current)) {
          map.removeLayer(osmLayerRef.current);
        }
        if (!map.hasLayer(satelliteLayerRef.current)) {
          map.addLayer(satelliteLayerRef.current);
          map.addLayer(labelsLayerRef.current);
        }
      } else {
        if (map.hasLayer(satelliteLayerRef.current)) {
          map.removeLayer(satelliteLayerRef.current);
          map.removeLayer(labelsLayerRef.current);
        }
        if (!map.hasLayer(osmLayerRef.current)) {
          map.addLayer(osmLayerRef.current);
        }
      }
    }
  }, [activeLayer, onMapReady, onDrawCreated]);

  return (
    <div ref={mapRef} className="map-container">
      {children}
    </div>
  );
}

export default MapComponent;
